/**
 * In-app confirmation dialog — replaces the browser's native window.confirm(), which
 * is unstyled, off-brand, and cannot be themed. `useConfirm()` returns an async
 * `confirm(opts)` that resolves true/false when the user chooses, so a call site reads
 * almost the same as before: `if (!(await confirm({ title }))) return;`.
 */
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';

import { Dialog } from '@/components/overlay';

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; opts: ConfirmOptions }>({
    open: false,
    opts: { title: '' },
  });
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setState({ open: true, opts });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    setState((s) => ({ ...s, open: false }));
    resolver.current?.(value);
    resolver.current = null;
  };

  const { open, opts } = state;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onClose={() => settle(false)} title={opts.title}>
        {opts.body ? <p className="text-sm text-ink-muted">{opts.body}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => settle(false)}
            className="rounded-field border border-border px-4 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => settle(true)}
            className={`rounded-field px-4 py-2 text-sm font-semibold text-white shadow-soft transition ${
              opts.danger ? 'bg-danger hover:bg-danger/90' : 'bg-primary hover:bg-primary-light'
            }`}
          >
            {opts.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
